import React, { useCallback, useEffect, useMemo, useState } from "react"
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete"
import { TextField } from "@material-ui/core"
import debounce from "lodash.debounce"

const DEBOUNCE_MS = 200

const defaultFilterOptions = createFilterOptions({
  stringify: option => option?.label || "",
})

const mergeTrailItems = (items, trailItems) => {
  if (!trailItems.length) return items

  const itemIds = new Set(items.map(item => item?.id))
  const trailing = trailItems.filter(item => item?.id && !itemIds.has(item.id))
  return items.concat(trailing)
}

const OptimizedUserAutocomplete = ({
  options = [],
  optionsById,
  value,
  onChange,
  label,
  multiple = false,
  trailItems = [],
  renderOption,
  error = false,
  helperText = "",
  onBlur,
  id,
  name,
  fullWidth = true,
  size = "small",
  noOptionsText = "sin opciones",
  disabled = false,
  renderTags,
  variant = "outlined",
  ...rest
}) => {
  const [inputValue, setInputValue] = useState("")
  const [debouncedInputValue, setDebouncedInputValue] = useState("")

  const debouncedSetInput = useMemo(
    () => debounce(nextValue => setDebouncedInputValue(nextValue), DEBOUNCE_MS),
    []
  )

  useEffect(() => () => debouncedSetInput.cancel(), [debouncedSetInput])

  const handleInputChange = useCallback(
    (_event, nextValue, reason) => {
      if (reason === "reset") return
      setInputValue(nextValue)
      debouncedSetInput(nextValue)
    },
    [debouncedSetInput]
  )

  const resolvedValue = useMemo(() => {
    if (multiple) {
      if (!Array.isArray(value)) return []
      return value
        .map(item => {
          if (typeof item === "object" && item !== null) return item
          return optionsById?.get(item) || null
        })
        .filter(Boolean)
    }

    if (!value) return null
    if (typeof value === "object") return value
    return optionsById?.get(value) || null
  }, [multiple, optionsById, value])

  useEffect(() => {
    if (multiple) return

    if (!resolvedValue) {
      setInputValue("")
      setDebouncedInputValue("")
      return
    }

    setInputValue(resolvedValue.label || "")
    setDebouncedInputValue(resolvedValue.label || "")
  }, [multiple, resolvedValue])

  const filterOptions = useCallback(
    (items, state) => {
      const trimmedInput = debouncedInputValue.trim()

      if (!trimmedInput) {
        return mergeTrailItems(items, trailItems)
      }

      return mergeTrailItems(
        defaultFilterOptions(items, {
          ...state,
          inputValue: debouncedInputValue,
        }),
        trailItems
      )
    },
    [debouncedInputValue, trailItems]
  )

  const defaultRenderOption = useCallback(
    option => <span>{option?.label || ""}</span>,
    []
  )

  return (
    <Autocomplete
      {...rest}
      id={id}
      disabled={disabled}
      multiple={multiple}
      fullWidth={fullWidth}
      options={options}
      filterOptions={filterOptions}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      value={resolvedValue}
      onChange={onChange}
      onBlur={onBlur}
      getOptionLabel={option => option?.label || ""}
      getOptionSelected={(option, selected) => option?.id === selected?.id}
      renderOption={renderOption || defaultRenderOption}
      noOptionsText={noOptionsText}
      renderTags={renderTags}
      renderInput={params => (
        <TextField
          {...params}
          name={name}
          label={label}
          variant={variant}
          placeholder="Buscar..."
          size={size}
          error={error}
          helperText={helperText}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
        />
      )}
    />
  )
}

export default OptimizedUserAutocomplete
